import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { STATUS_COLORS } from '../utils/mapIcons';

/**
 * @file MapControls.jsx
 * @description Internal map handlers (bounds, focus) and UI controls (legend, reset zoom).
 */

/**
 * Handler for automatic map bounds adjustment based on projects.
 */
export const MapBoundsHandler = ({ projects, isFocusing }) => {
    const map = useMap();
    const lastProjectsCount = useRef(0);
    const hasFittedInitial = useRef(false);

    useEffect(() => {
        const markers = projects.filter(p => p.latitude && p.longitude);
        
        if (isFocusing) {
            hasFittedInitial.current = true;
            lastProjectsCount.current = markers.length;
            return;
        }

        if (markers.length === 0) return;

        if (markers.length !== lastProjectsCount.current || !hasFittedInitial.current) {
            const bounds = L.latLngBounds(markers.map(p => [p.latitude, p.longitude]));
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
            lastProjectsCount.current = markers.length;
            hasFittedInitial.current = true;
        }
    }, [projects, map, isFocusing]);

    return null;
};

/**
 * Handler for focusing the map on a specific project.
 */
export const MapFocuser = ({ focusedProject, onCleared }) => {
    const map = useMap();

    useEffect(() => {
        if (focusedProject && focusedProject.latitude && focusedProject.longitude) {
            const zoomTimer = setTimeout(() => {
                map.setView([focusedProject.latitude, focusedProject.longitude], 17, { animate: true });
                
                setTimeout(() => {
                    map.eachLayer((layer) => {
                        if (layer instanceof L.Marker && layer.options.projectData?.id === focusedProject.id) {
                            layer.openPopup();
                        }
                    });
                    if (onCleared) onCleared();
                }, 500);
            }, 100);

            return () => clearTimeout(zoomTimer);
        }
    }, [focusedProject, map, onCleared]);

    return null;
};

/**
 * Button to reset the map zoom to show all projects.
 */
export const ResetZoomControl = ({ projects }) => {
    const map = useMap();
    const [isVisible, setIsVisible] = useState(false);

    const fitAll = useCallback(() => {
        const markers = projects.filter(p => p.latitude && p.longitude);
        if (markers.length === 0) return;
        const bounds = L.latLngBounds(markers.map(p => [p.latitude, p.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        setIsVisible(false);
    }, [projects, map]);

    useMapEvents({
        zoomend: () => setIsVisible(true),
        dragend: () => setIsVisible(true),
    });

    if (!isVisible) return null;

    return (
        <div className="leaflet-top leaflet-left" style={{ marginTop: '85px', marginLeft: '10px', zIndex: 1000 }}>
            <button 
                className="reset-zoom-btn"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fitAll();
                }}
                title="Übersicht wiederherstellen"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                </svg>
            </button>
        </div>
    );
};

/**
 * Legend showing the meaning of status colors.
 */
export const MapLegend = () => {
    return (
        <div style={{
            position: 'absolute',
            bottom: '45px',
            right: '15px',
            background: 'rgba(255,255,255,0.95)',
            padding: '8px 12px',
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            zIndex: 1000,
            fontSize: '0.75rem',
            border: '1px solid #e5e7eb',
            pointerEvents: 'none'
        }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '0.8rem', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>Status</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {Object.entries(STATUS_COLORS).map(([status, color]) => (
                    <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: color }}></span>
                        {status === 'active' ? 'Aktiv' : status === 'paused' ? 'Inaktiv' : status === 'completed' ? 'Abgeschlossen' : 'Archiv'}
                    </div>
                ))}
            </div>
        </div>
    );
};
